import React, { useState, useEffect } from "react";
import { Box, Button, Typography, TextField, IconButton } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { dotStream } from "ldrs";
import ShadowChartCard from "./ShadowChart";
import { detectTimesFromInput } from "./textProcessing";

import { LogflareService } from "../../../crashLogging/LogFlare";
dotStream.register();

const Question = ({
  onAnswerSelected,
  messages,
  sunHeightData,
  horizonHeightData,
  productionData,
  productionDataChange,
}) => {
  const [shadowLoss, setShadowLoss] = useState(0);
  const [energyLoss, setEnergyLoss] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [customAnswer, setCustomAnswer] = useState("");
  const [displayedText, setDisplayedText] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [showText, setShowText] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const chartOptions = {
    chart: { id: "sun-vs-horizon", type: "line", height: "auto" },
    stroke: { curve: "smooth" },
    title: { text: "Sun Height vs Horizon Height", align: "center" },
    xaxis: { categories: Array.from({ length: 12 }, (_, i) => `${i + 1} AM`) },
    yaxis: { title: { text: "Height (degrees)" } },
  };

  const chartSeries = [
    { name: "Sun Height", data: sunHeightData },
    { name: "Horizon Height", data: horizonHeightData },
  ];

  // Percentage Calculation Function
  const calculateCoveragePercentage = (detectedInputs) => {
    const totalHours = 13; // Hours from 7 AM to 7 PM inclusive
    const validHourRange = Array.from({ length: totalHours }, (_, i) => i + 7); // [7, 8, ..., 19]

    // Collect all detected hours
    const allDetectedHours = detectedInputs
      .map((entry) => entry.hours || []) // Extract hours, handle empty entries
      .flat();

    // Normalize by removing duplicates and constraining to valid hours
    const uniqueHours = new Set(
      allDetectedHours.filter((hour) => validHourRange.includes(hour))
    );

    // Calculate the coverage percentage
    const coveredHoursCount = uniqueHours.size;
    return (coveredHoursCount / totalHours) * 100;
  };
  const calculateEnergyLoss = (detectedInputs) => {
    const peakHours = [11, 12, 13]; // Peak solar production hours
    const totalMonthsInYear = 12;
    const peakHourLossPerMonth = 20 / 12; // 15% loss for each peak hour
    const nonPeakHourLossPerMonth = 40 / 9 / 12; // 7% loss for each non-peak hour

    let totalLoss = 0;

    // Calculate energy loss
    detectedInputs.forEach((entry, index) => {
      const { hours, months } = entry;
      const monthsAffected = months?.length || 0;

      if (monthsAffected === 0) {
        return;
      }

      (hours || []).forEach((hour) => {
        if (peakHours.includes(hour)) {
          const loss = peakHourLossPerMonth * monthsAffected;
          totalLoss += loss;
        } else {
          const loss = nonPeakHourLossPerMonth * monthsAffected;
          totalLoss += loss;
        }
      });
    });

    // Constrain loss to 100% maximum
    if (totalLoss > 100) {
      return 100;
    }

    return totalLoss;
  };

  // Simulate a "calculation" behind the scenes
  const performCalculation = async () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve("Calculation Complete");
      }, 3000); // Simulates a 3-second calculation
    });
  };

  useEffect(() => {
    if (isProcessing) {
      performCalculation().then((result) => {
        setShowCharts(true); // Show the charts after calculation
        setIsProcessing(false); // Stop the animation and text
        setShowText(false);
      });
    }
  }, [isProcessing]);

  useEffect(() => {
    if (showText) {
      const interval = setInterval(() => {
        setTextIndex((prevIndex) => {
          const newIndex = prevIndex + 1;
          if (newIndex >= messages.length) {
            clearInterval(interval);
          }
          return newIndex < messages.length ? newIndex : prevIndex;
        });
      }, 1000); // Update text every second
      return () => clearInterval(interval);
    }
  }, [showText, messages]);

  useEffect(() => {
    if (textIndex < messages.length) {
      setDisplayedText(messages[textIndex]);
    }
  }, [textIndex, messages]);

  const handleAnswerSelect = (answer) => {
    const detectedTimes = detectTimesFromInput(answer);
    LogflareService.sendDataToLogflare(answer, 0);
    setShadowLoss(calculateCoveragePercentage(detectedTimes));
    setEnergyLoss(calculateEnergyLoss(detectedTimes));
    setSelectedAnswer(answer);
    setCustomAnswer("");
    onAnswerSelected(detectedTimes);
    setShowText(true);
    setIsProcessing(true); // Start the calculation
  };

  const handleSendAnswer = () => {
    if (customAnswer.trim() !== "") {
      const detectedTimes = detectTimesFromInput(customAnswer);
      LogflareService.sendDataToLogflare(customAnswer, 0);
      setShadowLoss(calculateCoveragePercentage(detectedTimes));
      setEnergyLoss(calculateEnergyLoss(detectedTimes));
      setSelectedAnswer(customAnswer);
      setCustomAnswer("");
      onAnswerSelected(detectedTimes);
      setShowText(true);
      setIsProcessing(true); // Start the calculation
      //onAnswerSelected(customAnswer);
    }
  };
  const resetProcess = () => {
    setSelectedAnswer(null); // Reset selected answer
    setCustomAnswer(""); // Clear custom answer input
    setDisplayedText(""); // Clear displayed text
    setTextIndex(0); // Reset text index
    setShowText(false); // Hide text
    setShowCharts(false); // Hide charts
    setIsProcessing(false); // Ensure no ongoing processing
  };

  return (
    <>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          mb: 5,
          fontSize: { xs: "1.2rem", sm: "1.5rem" },
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        How shadowed is your installation site?
      </Typography>

      {!selectedAnswer && (
        <>
          <Box sx={{ mb: 4 }}>
            {[
              "Never, it is always sunny",
              "My site is shadowed at 5 PM in summer.",
              "My site is shadowed from 8 AM to 10 AM in winter.",
              "My site is shadowed in the morning in spring.",
            ].map((option, idx) => (
              <Button
                key={idx}
                fullWidth
                variant="contained"
                onClick={() => handleAnswerSelect(option)}
                sx={{
                  textTransform: "none",
                  display: "flex",
                  justifyContent: "flex-start",
                  backgroundColor: "#1c1c28",
                  color: "#fff",
                  py: 2,
                  mb: 2,
                  fontSize: { xs: "0.8rem", sm: "1rem" },
                  "&:hover": { backgroundColor: "#34344a" },
                }}
              >
                {option}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="My site is shadowed..."
              value={customAnswer}
              onChange={(e) => setCustomAnswer(e.target.value)}
              InputProps={{
                style: { color: "#fff", backgroundColor: "#1c1c28" },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": { borderColor: "#fff" },
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendAnswer}
              disabled={!customAnswer.trim()}
              sx={{
                padding: { xs: "8px", sm: "10px" },
                alignSelf: "stretch",
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </>
      )}

      {isProcessing && (
        <Box
          sx={{
            height: "90%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            mb: 3,
            mt: 5,
            paddingTop: "32px",
          }}
        >
          <l-dot-stream size="120" speed="2.5" color="#00d8a8"></l-dot-stream>
        </Box>
      )}

      {showText && (
        <Typography
          variant="body1"
          sx={{
            textTransform: "none",
            color: "#fff",
            textAlign: "center",
            mt: 3,
            fontSize: { xs: "0.9rem", sm: "1rem" },
            transition: "opacity 0.3s ease",
          }}
        >
          {displayedText}
        </Typography>
      )}

      {showCharts && (
        <ShadowChartCard
          handleRestartClick={resetProcess}
          shadowLoss={shadowLoss.toFixed(0)}
          energyLoss={energyLoss.toFixed(0)}
        />
      )}
    </>
  );
};

export default Question;
